/* eslint-disable react-hooks/exhaustive-deps */
import { PropTypes } from 'prop-types';
import { useEffect, useState, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button, Paper, Popper, TextField, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import Utils from '@/common/utils/utils';
import Constant from '@/common/constant/Constant';
import { getPublicAPI } from '@/service/publicService';

import OverFlowTooltipWithTabsMui from '@/component/common/OverFlowTooltipWithTabsMui';
import useDebounce from '@/hooks/useDebounce';
import ListboxWithInfiniteScroll from '../ListboxWithInfiniteScroll';
import { normalizeString, onKeyPressSearchKeyWord, onSelectSugguestOption, onSubmitSearchListKnowledge } from '../../handler/ListTicketKnowledgeHandler';

const SearchInput = ({
    argsCommon,
    drillDownData,
    viewStructure,
    inputKeyWordRef,
    keywordSearch,
    setKeywordSearch,
    suggestionList,
}) => {
    const useStyles = makeStyles(theme => ({
    root: {
            display: 'flex',
            alignItems: 'center',
            flexGrow: 1,
            border: 'none',
            boxShadow: 'unset',
            borderRadius: '0px',
            '& .MuiTextField-root': {
                margin: '0',
                width: '100%'
            }
        },
        input: {
            marginLeft: theme.spacing(1),
            flex: 1,
            width: '100%'
        },
        iconButton: {
            padding: 10
        },
        divider: {
            height: '100%',
            margin: 4
        },
        arrowIcon: {
            fill: 'white'
        }
    }));
    const searchInputClasses = useStyles();

    const [localInput, setLocalInput] = useState(keywordSearch);
    const debouncedKeyword = useDebounce(localInput, 200);
    const [isSearching, setIsSearching] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState([]);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const scrollPositionRef = useRef(0);

    // when change view, check viewStructure?.keyword
    useEffect(() => {
        setLocalInput(`${viewStructure?.keyword}` || '')
    }, [viewStructure])

    // listen keywordInput to query searchOptionList
    useEffect(() => {
        setKeywordSearch(debouncedKeyword);
        setHasMore(true);
        scrollPositionRef.current = 0;
        if (!debouncedKeyword) {
            setFilteredOptions([]);
            return;
        }
        
        const isEmptyDrilldown = Utils.isEmptyArray(drillDownData);
        setIsSearching(isEmptyDrilldown);
        if (isEmptyDrilldown) {
            return;
        }

        setIsSearching(true);

        handleSearchSuggestionList(debouncedKeyword)
            .then(res => {
                const merged = [...suggestionList, ...res];
                const uniqueList = Array.from(new Map(merged.map(item => [item.id, item])).values());
                const render = renderFilteredOptions(debouncedKeyword, uniqueList);
                setFilteredOptions([...render]);
            })
            .catch(err => {
                console.log('handleSearchSuggestionList err :', err );
            })
            .finally(() => {
                setIsSearching(false);
            })
        return () => {}
    }, [debouncedKeyword, suggestionList, drillDownData])

    const renderFilteredOptions = (keyword, optionList) => {
        if (!keyword) return [];
        const normalizeStringKeyword = normalizeString(keyword);

        const result = optionList.filter(opt =>
            normalizeString(opt?.labelForSearch).includes(normalizeStringKeyword)
        );
        return result;
    };

    const handleSearchSuggestionList = async (queryKeyword = '', lastItem = null) => {
        let listOptionS3 = drillDownData.filter(option => option.itemListS3Path);
        if (Utils.isEmptyArray(listOptionS3)) return [];
        listOptionS3 = listOptionS3.map(option => {
            const { child, ...rest } = option; 
            return rest;
        });
        const params = {
            getApiType: Constant.GET_API_PUBLIC_TYPE.DRILLDOWN_GET_SUGGEST_TAG_OPTIONS,
            listOptionS3: listOptionS3,
            limit: Constant.DRILLDOWN_SUGGEST_OPTION_VISIBLE_SIZE,
            lastItem: lastItem,
            queryKeyword: queryKeyword,
            panelType: Constant.PANEL_TYPE.KNOWLEDGE_TICKET_LIST,
            clientListId: filteredOptions.map(opt => ({id: opt.id, groupId: opt.groupId}))
        };
        try {
            const res = await getPublicAPI(null, params);
            const listSuggestOptions = Utils.convertJSONDataToObject(res?.payload)?.listSuggestOptions || [];
            return listSuggestOptions;
        } catch (err) {
            console.log("scroll listOptionS3 err >>> ", err);
            return [];
        }
    };

    const handleLoadMore = async (queryKeyword = '', lastItem = null) => {
        const more = await handleSearchSuggestionList(queryKeyword, lastItem);
        setFilteredOptions(prev => {
            const merged = [...prev, ...more];
            const uniqueList = Array.from(new Map(merged.map(item => [item.id, item])).values());
            return uniqueList
        });
        return more;
    }

    return (
        <Paper className={searchInputClasses.root}>
            <Autocomplete
                PopperComponent={({ style, ...props }) => (
                    <Popper {...props} style={{ ...style }} className="selectDrop" />
                )}
                className="search-keyword"
                freeSolo
                disablePortal
                sx={{maxHeight: 10}}

                loading={isSearching}
                loadingText={<CircularProgress color="primary" size="1rem" />}
                options={filteredOptions}
                filterSelectedOptions
                getOptionLabel={option => option.label || ''}
                renderOption={option => <OverFlowTooltipWithTabsMui value={option.label} />}
                ListboxComponent={(props) => (
                    <ListboxWithInfiniteScroll
                        {...props}
                        optionList={filteredOptions}
                        isLoadingMore={isLoadingMore}
                        setIsLoadingMore={setIsLoadingMore}
                        handleLoadMore={handleLoadMore}
                        searchKeyword={debouncedKeyword}
                        scrollPositionRef={scrollPositionRef}
                        hasMore={hasMore}
                        setHasMore={setHasMore}
                    />
                )}
                onChange={(e, value) => onSelectSugguestOption(e, value, argsCommon)}
                
                inputValue={localInput}
                renderInput={params => (
                    <TextField
                        {...params}
                        placeholder="キーワードを入力してください"
                        value={localInput}
                        onChange={e => setLocalInput(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') {
                                if (localInput !== keywordSearch) {
                                    setKeywordSearch(localInput);
                                    let argsCommonClone = {...argsCommon};
                                    argsCommonClone.keywordSearch = localInput;
                                    onKeyPressSearchKeyWord(e, argsCommonClone)
                                    e.stopPropagation();
                                    return;
                                }
                                
                                onKeyPressSearchKeyWord(e, argsCommon)
                                e.stopPropagation();
                            }
                        }}
                        inputRef={inputKeyWordRef}
                        variant="outlined"
                    />
                )}
            />
            <Button
                className="btn-icon-search"
                variant="contained"
                color="primary"
                onClick={e => onSubmitSearchListKnowledge(argsCommon)}
            >
                <img src="/assets/image/icon-searchFrame.svg" alt="" />
            </Button>
        </Paper>
    )
}

SearchInput.propTypes = {
	argsCommon: PropTypes.any,
	drillDownData: PropTypes.any,
	viewStructure: PropTypes.any,
	inputKeyWordRef: PropTypes.any,
	keywordSearch: PropTypes.any,
	setKeywordSearch: PropTypes.any,
	suggestionList: PropTypes.any,
};

export default SearchInput;